// Operatör ana sayfası (mockup panel/PagesOperate.jsx InternalOverview).
//
// Yalnız backend'i olan paneller çizilir; her panel kendi uç noktasının
// rolüyle gate'lenir ve rolü olmayan operatör paneli hiç görmez. Mockup'taki
// açık olaylar, destek kuyruğu, MRR, bölge gecikmesi ve sürüm penceresi
// panellerinin operatör API'sinde karşılığı yok; çizilmez.
//
// Buradaki her okuma (dizin sayısı, sağlık, denetim) backend'de bir operatör
// denetim satırı yazar. Bu yüzden hiçbir panel refetchInterval kullanmaz ve
// sorgu anahtarları kendi sayfalarıyla ortaktır: ana sayfadan /health'e geçiş
// ikinci bir okuma satırı üretmez.

import { useQuery } from "@tanstack/react-query";
import { Activity, Building2, Server, UserCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import {
  Badge,
  Card,
  CardHeader,
  Empty,
  ErrorState,
  Loading,
  PageHeader,
  StatCard,
  type BadgeTone,
} from "@novemai/components";

import { unwrap } from "@/api/envelope";
import { fetchHealthz, operatorGraph } from "@/api/operator-graph";
import { RequireAction } from "@/components/RequireRole";
import { usePermission } from "@/hooks/usePermission";
import { resolveErrorDisplay } from "@/lib/envelope-error-display";
import { sloName } from "@/lib/slo-name";
import { tenantCountQueryOptions } from "@/lib/tenant-queries";
import { cn } from "@/lib/utils";

const RECENT_AUDIT_LIMIT = 5;
const ATTENTION_LIMIT = 5;

const SLO_TONE: Readonly<Record<string, BadgeTone>> = {
  red: "danger",
  yellow: "warning",
};

function fetchHealth() {
  return unwrap(operatorGraph.GET("/ops/v1/health"));
}

const panelLinkClass =
  "font-sans text-[12.5px] font-medium text-[var(--accent)] no-underline hover:underline";

export function HomePage(): React.ReactElement {
  const { t } = useTranslation();
  const canReadAudit = usePermission("audit.read");
  const canReadHealth = usePermission("health.read");

  return (
    <div className="mx-auto max-w-[1320px]">
      <PageHeader
        title={t("home.welcome", { appName: t("app.title") })}
        description={t("home.lede")}
      />

      <div className="mb-6 grid grid-cols-2 gap-4 xl:grid-cols-4">
        <RequireAction action="tenants.read">
          <ActiveTenantsTile />
        </RequireAction>
        <RequireAction action="health.read">
          <SloBreachTile />
        </RequireAction>
        <RequireAction action="impersonate.read">
          <ActiveSessionsTile />
        </RequireAction>
        <ApiStatusTile />
      </div>

      {(canReadAudit || canReadHealth) && (
        <div
          className={cn(
            "grid items-start gap-6",
            canReadAudit && canReadHealth && "lg:grid-cols-[1.5fr_1fr]",
          )}
        >
          {canReadAudit && <RecentAuditCard />}
          {canReadHealth && <SloAttentionCard />}
        </div>
      )}
    </div>
  );
}

function ActiveTenantsTile(): React.ReactElement {
  const { t } = useTranslation();
  const query = useQuery(tenantCountQueryOptions("active"));
  return (
    <StatCard
      label={t("home.kpi.active_tenants")}
      value={query.data === undefined ? t("common.empty_value") : `${query.data}`}
      tone="accent"
      icon={<Building2 size={18} />}
    />
  );
}

function SloBreachTile(): React.ReactElement {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: ["operator", "health"], queryFn: fetchHealth });
  const breaches = query.data?.breach_count;
  return (
    <StatCard
      label={t("home.kpi.slo_breaches")}
      value={breaches === undefined ? t("common.empty_value") : `${breaches}`}
      tone={breaches !== undefined && breaches > 0 ? "danger" : "success"}
      icon={<Activity size={18} />}
    />
  );
}

function ActiveSessionsTile(): React.ReactElement {
  const { t } = useTranslation();
  // impersonations.tsx'in varsayılan görünümüyle aynı anahtar: backend
  // parametresiz isteği çağıranın kendi oturumlarına daraltır.
  const query = useQuery({
    queryKey: ["operator", "impersonations", { includeAll: false }],
    queryFn: () =>
      unwrap(operatorGraph.GET("/ops/v1/impersonations/active", { params: { query: {} } })),
  });
  return (
    <StatCard
      label={t("home.kpi.my_sessions")}
      value={query.data === undefined ? t("common.empty_value") : `${query.data.total}`}
      tone="info"
      icon={<UserCog size={18} />}
    />
  );
}

function ApiStatusTile(): React.ReactElement {
  const { t } = useTranslation();
  // Healthz yalnız açılışta, refetchInterval yok: uç her istekte bir denetim
  // satırı yazıyor; her operatör sekmesinden yoklamak tabloyu boşuna doldurur.
  const healthz = useQuery({
    queryKey: ["operator-graph", "healthz"],
    queryFn: fetchHealthz,
    staleTime: Infinity,
  });
  const statusKey = healthz.isLoading
    ? "home.backend_status_loading"
    : healthz.isError
      ? "home.backend_status_error"
      : "home.backend_status_ok";
  return (
    <StatCard
      label={t("home.backend_status_label")}
      value={t(statusKey)}
      tone={healthz.isError ? "danger" : healthz.isLoading ? "neutral" : "success"}
      icon={<Server size={18} />}
    />
  );
}

function RecentAuditCard(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: ["operator", "audit", { recent: RECENT_AUDIT_LIMIT }],
    queryFn: () =>
      unwrap(
        operatorGraph.GET("/ops/v1/audit", { params: { query: { limit: RECENT_AUDIT_LIMIT } } }),
      ),
    staleTime: 60_000,
  });
  const fmtAt = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );
  const events = query.data?.events ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="nai-card-title">{t("home.recent_audit_title")}</div>
          <RouterLink to="/audit" className={panelLinkClass}>
            {t("home.open_audit")}
          </RouterLink>
        </div>
      </CardHeader>
      {query.isLoading ? (
        <Loading label={t("audit.loading")} rows={3} />
      ) : query.isError ? (
        <ErrorState
          title={t("audit.error_title")}
          description={resolveErrorDisplay(query.error, t).description}
        />
      ) : events.length === 0 ? (
        <Empty title={t("audit.empty_title")} description={t("audit.empty_desc")} />
      ) : (
        <ul className="m-0 list-none p-0">
          {events.map((event, i) => (
            <li
              key={event.event_id}
              className={cn(
                "px-[22px] py-3",
                i > 0 && "border-t border-[var(--border-subtle)]",
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-serif text-[13.5px] font-medium text-[var(--fg-primary)]">
                  {event.operator_name ?? event.operator_id}
                </span>
                <span className="rounded-[5px] bg-[var(--bg-subtle)] px-1.5 py-0.5 font-mono text-[11.5px] text-[var(--fg-primary)]">
                  {event.action}
                </span>
              </div>
              <div className="mt-0.5 font-serif text-[12px] italic text-[var(--fg-muted)]">
                {event.target_tenant_id !== null
                  ? t("home.audit_meta_tenant", {
                      at: fmtAt(event.at),
                      tenant: event.target_tenant_name ?? event.target_tenant_id,
                    })
                  : fmtAt(event.at)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function SloAttentionCard(): React.ReactElement {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: ["operator", "health"], queryFn: fetchHealth });
  const attention = (query.data?.slos ?? []).filter(
    (slo) => slo.composite_status === "red" || slo.composite_status === "yellow",
  );
  // Kırmızı önce: ihlal, bütçeyi hızlı yakan servisten daha acil.
  attention.sort((a, b) => (a.composite_status === b.composite_status ? 0 : a.composite_status === "red" ? -1 : 1));
  const total = query.data?.total ?? 0;
  const unknown = query.data?.unknown_count ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="nai-card-title">{t("home.slo_attention_title")}</div>
          <RouterLink to="/health" className={panelLinkClass}>
            {t("home.open_health")}
          </RouterLink>
        </div>
      </CardHeader>
      {query.isLoading ? (
        <Loading label={t("health.loading")} rows={3} />
      ) : query.isError ? (
        <ErrorState
          title={t("health.error_title")}
          description={resolveErrorDisplay(query.error, t).description}
        />
      ) : attention.length === 0 ? (
        // "Hepsi yolunda" ancak değerlendirme verisi varsa söylenebilir:
        // değerlendirici koşmuyorsa bütün SLO'lar "unknown" döner.
        <Empty
          title={
            total > 0 && unknown === total
              ? t("home.slo_no_data_title")
              : t("home.slo_all_clear_title")
          }
          description={
            total > 0 && unknown === total
              ? t("home.slo_no_data_desc")
              : t("home.slo_all_clear_desc", { count: total - unknown })
          }
        />
      ) : (
        <ul className="m-0 list-none p-0">
          {attention.slice(0, ATTENTION_LIMIT).map((slo, i) => (
            <li
              key={slo.slo_id}
              className={cn(
                "flex items-center gap-3 px-[22px] py-3",
                i > 0 && "border-t border-[var(--border-subtle)]",
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-serif text-[13.5px] text-[var(--fg-primary)]">
                  {sloName(t, slo.slo_id)}
                </div>
                <div className="font-mono text-[11px] text-[var(--fg-muted)]">{slo.spec_id}</div>
              </div>
              <Badge tone={SLO_TONE[slo.composite_status] ?? "neutral"}>
                {t(`health.status.${slo.composite_status}`)}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
